import { useEffect, useState } from "react"

import { usePublicBoundlessConfigRetrieveSuspense } from "@boundless/bl-api-client/public/services"
import { useRouterState, useSearch } from "@tanstack/react-router"
import { useStore } from "zustand"

import { AccountHeaderLink } from "@/components/account/layout/AccountHeaderLink"
import { MobileProofScreen } from "@/components/account/layout/MobileProofScreen"
import { QualificationPanel } from "@/components/account/layout/QualificationPanel"
import { AccountFormColumnProvider } from "@/components/account/layout/StickyFormActions"
import { store } from "@/lib/store"
import BoundlessLogo from "@/svg/boundless/logo.svg?react"
import BoundlessLogoGray from "@/svg/boundless/logo_gray.svg?react"

type AccountLayoutProps = {
  children: React.ReactNode
  logo?: string
}

/** The two entry points, which carry a header link across to each other. */
const ENTRY_ROUTE = /\/(login|signup)$/

/** Tailwind's `lg`, where the layout switches to proof and form side by side. */
const DESKTOP_QUERY = "(min-width: 64rem)"

/**
 * Tracks the breakpoint in JavaScript rather than with a utility class, because
 * the proof screen decides what is *mounted*, not just what is painted. Hiding
 * the form with `display: none` instead would leave Turnstile initialising
 * against a zero-sized target it never re-measures.
 */
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches,
  )

  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY)
    const sync = () => setIsDesktop(query.matches)

    sync()
    query.addEventListener("change", sync)
    return () => query.removeEventListener("change", sync)
  }, [])

  return isDesktop
}

export function AccountLayout({ children }: AccountLayoutProps) {
  const partnerLogo = useStore(store, (state) => state.partnerLogo)
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const search = useSearch({ strict: false })
  const { data: config } = usePublicBoundlessConfigRetrieveSuspense()
  const isDesktop = useIsDesktop()
  const [hasLeftProof, setHasLeftProof] = useState(false)

  const isEntryRoute = ENTRY_ROUTE.test(pathname)
  const isSignup = pathname.endsWith("/signup")

  // Only signup opens on the proof screen. `/` redirects to `/login`, so login
  // is where returning borrowers land, and the design sends them straight to
  // the form. The rest of the account routes (password reset, invitations) are
  // places a borrower was sent on purpose, where a pitch would be in the way.
  //
  // Signup falls back to a message when it is switched off, so a pitch whose
  // only action is "Create account" would front a dead end.
  //
  // `?verified` and `?resetComplete` render results the borrower was sent to
  // deliberately, and `?verified=false` is the expired-link error, so it is the
  // presence of the param that disqualifies the pitch, not its value.
  const showsProof =
    !hasLeftProof &&
    isSignup &&
    !isDesktop &&
    config.signupEnabled &&
    search.verified === undefined &&
    search.resetComplete === undefined

  // Crossing the breakpoint decides what is mounted, so narrowing a desktop
  // window would otherwise throw away a part-filled form. Desktop has already
  // shown the proof alongside the form, so treat it as seen.
  useEffect(() => {
    if (isDesktop) {
      setHasLeftProof(true)
    }
  }, [isDesktop])

  return (
    <div className="flex h-svh flex-col lg:grid lg:grid-flow-col lg:grid-cols-[3fr_5fr] lg:grid-rows-[minmax(0,1fr)_auto]">
      {showsProof ? (
        <MobileProofScreen
          onCreateAccount={() => setHasLeftProof(true)}
          headerLink={
            <AccountHeaderLink
              target="login"
              className="text-[13px] text-white/85 underline"
              onClick={() => setHasLeftProof(true)}
            />
          }
        />
      ) : (
        <>
          {/* On desktop the spacing and logo scale with the viewport height,
              so a short laptop screen keeps the same proportions as a tall
              one instead of pushing the submit button below the fold. */}
          <div className="mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col lg:px-8">
            <div className="flex items-center justify-between px-6 pt-4 lg:block lg:px-0 lg:pt-[min(6rem,7svh)] lg:pb-[min(2rem,2.5svh)]">
              {partnerLogo ? (
                <img
                  src={partnerLogo}
                  className="max-h-10 w-28 object-contain object-left lg:max-h-[min(4rem,6svh)] lg:w-auto lg:max-w-xs"
                />
              ) : (
                <BoundlessLogo className="w-28 lg:h-[min(4rem,6svh)] lg:w-auto lg:max-w-xs" />
              )}
              {isEntryRoute && (
                <AccountHeaderLink
                  target={isSignup ? "login" : "signup"}
                  className="text-[13px] text-neutral-600 underline lg:hidden"
                />
              )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-5 lg:px-0 lg:pt-0">
              <AccountFormColumnProvider>{children}</AccountFormColumnProvider>
            </div>
          </div>

          <div className="mx-auto mt-2 self-center pb-8 lg:pb-[min(2rem,3svh)]">
            <p className="text-center text-sm text-neutral-500">Powered by</p>
            <div className="mx-auto w-40">
              <BoundlessLogoGray />
            </div>
          </div>
        </>
      )}

      <div className="row-span-2 hidden lg:block">
        <QualificationPanel />
      </div>
    </div>
  )
}
